import pluralize from "pluralize"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import DataTableV2 from "src/components/DataTableV2"
import Form from "src/components/Form"
import Heading from "src/components/Heading"
import Tooltip from "src/components/Tooltip"

import { bulkUpdateProductsStatus } from "src/api/Manage/ProductsTable"

import { posAccessQueryKey } from "src/hooks/module_gate_hooks/use_pos_access"
import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import {
  formatCurrencyFromMinorUnits,
  formattedDecimalToTax,
} from "src/utils/UnitConversion"
import detectBarcodeScan from "src/utils/detectBarcodeScan"

import TaxCell from "../Billing/Items/TaxCell"
import ActiveItemLimitBanner from "./ActiveItemLimitBanner"
import AdjustQuantityModal from "./AdjustQuantityModal"
import { displayQuantityBalance } from "./AdjustQuantityModal/helpers"
import BulkQuantityAdjustmentImporter from "./BulkQuantityAdjustmentImporter"
import { ManageItemsContext } from "./ManageItemsContainer"
import ManageItemsFilters from "./ManageItemsFilters"
import NewItemButton from "./NewItemButton"
import StatusFilter from "./StatusFilter"
import {
  MANAGE_ADJUST_QTY,
  MANAGE_BULK_ADJUST_QTY,
  MANAGE_BULK_MAKE_INACTIVE,
  MANAGE_BULK_REACTIVATE,
  MANAGE_OVERFLOW_EDIT,
  MANAGE_OVERFLOW_MAKE_INACTIVE_CONFIRMED,
  MANAGE_SEARCH,
  MANAGE_STATUS_FILTER,
} from "./amplitude_events"
import flattenProducts from "./utils/flatten_products"

const ManageItemsTable = () => {
  const { presentmentCurrency } = useMarina()
  const {
    table,
    canManageItems,
    canAdjustQuantitiesViaImport,
    marinaSlug,
    marinaAccess,
    useNewTaxExperience,
    accountingV2Enabled,
    currentUserId,
  } = useContext(ManageItemsContext)
  const tracker = useTracker()
  const showToast = useToast()
  const queryClient = useQueryClient()

  const [searchTerm, setSearchTerm] = useState(table.filters.search || "")
  const [adjustQuantityItem, setAdjustQuantityItem] = useState(null)
  const [bulkAdjustSelection, setBulkAdjustSelection] = useState(null)

  const status = table.filters.status
  const rows = useMemo(
    () => flattenProducts(table.records, status),
    [table.records, status]
  )

  // Selection persists across content filters (category / search / barcode) so
  // a selection can be built across filtered views — the toolbar surfaces the
  // count. The status view is reset-worthy because it swaps the available bulk
  // actions (deactivate/adjust on Active vs reactivate on Inactive).
  const prevStatus = useRef(status)
  useEffect(() => {
    if (prevStatus.current === status) return
    prevStatus.current = status
    table.setSelection([])
  }, [status, table])

  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("search", value)
    if (value) tracker.trackEvent(MANAGE_SEARCH)
  })

  const handleSearch = (event) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSetSearch(value.trim())
  }

  // Hands-free barcode scan populates the search box and filters, unless the
  // user is actively typing in it. Kept in a ref so the listener attaches once.
  const searchFocused = useRef(false)
  const handleBarcodeScan = useRef(() => {})
  handleBarcodeScan.current = (barcode) => {
    if (searchFocused.current) return
    setSearchTerm(barcode)
    debouncedSetSearch(barcode)
  }

  useEffect(() => {
    if (!marinaAccess.barcodeScanner) return undefined
    return detectBarcodeScan((barcode) => handleBarcodeScan.current(barcode))
  }, [marinaAccess.barcodeScanner])

  const handleClearSearch = () => {
    setSearchTerm("")
    table.setFilter("search", "")
  }

  const handleClearAllFilters = () => {
    setSearchTerm("")
    table.clearAllFilters()
  }

  const refetchItemsData = () => {
    queryClient.invalidateQueries({ queryKey: ["manageItems", marinaSlug] })
    queryClient.invalidateQueries(posAccessQueryKey({ marinaSlug }))
  }

  const runBulk = (ids, bulkStatus, { clearSelection = false } = {}) =>
    bulkUpdateProductsStatus({ ids, status: bulkStatus })
      .then(({ succeededIds = [], failed = [] }) => {
        if (succeededIds.length) {
          showToast(
            `${succeededIds.length} ${pluralize(
              "item",
              succeededIds.length
            )} updated`,
            { type: "success" }
          )
        }
        failed.forEach((f) => showToast(f.error, { type: "error" }))
        if (clearSelection) table.setSelection([])
        refetchItemsData()
      })
      .catch(() =>
        showToast(
          "There was an issue updating the selected items. If the problem persists, please contact Dockwa at mayday@dockwa.com.",
          { type: "error" }
        )
      )

  const COLUMNS = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      cardRole: "title",
      width: "260px",
      render: (row) => (
        <div className="flex items-center truncate" title={row.name}>
          {row.isInventoryChild ? (
            <i
              className="fa-solid fa-arrow-down-right mr-2 text-gray-600"
              role="img"
            />
          ) : null}
          <span className="truncate">{row.name}</span>
          {row.deletedAt ? (
            <span className="ml-1 text-gray-500"> (inactive)</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "sku",
      header: "SKU",
      sortable: true,
      cardRole: "detail",
      width: "150px",
    },
    {
      key: "barcode",
      header: "Barcode",
      width: "120px",
      cardRole: "detail",
      render: (row) =>
        row.barcode ? (
          <i className="fa-solid fa-circle-check text-lg text-teal-500" />
        ) : null,
    },
    {
      key: "productCategory",
      header: "Category",
      width: "170px",
      sortable: true,
      cardRole: "detail",
      render: (row) => row.productCategory?.displayName || null,
    },
    {
      key: "price",
      header: "Default Price",
      width: "150px",
      cardRole: "detail",
      render: (row) =>
        row.pricePerUnit
          ? formatCurrencyFromMinorUnits({
              amount: row.pricePerUnit,
              currency: presentmentCurrency,
              precision: row.pricePrecision === "cents" ? 2 : 4,
            })
          : null,
    },
    {
      key: "tax",
      header: "Default Tax",
      width: "120px",
      cardRole: "detail",
      render: (row) =>
        useNewTaxExperience ? (
          <TaxCell taxes={row.taxes} currency={presentmentCurrency} unwrapped />
        ) : (
          row.taxRate && formattedDecimalToTax(row.taxRate)
        ),
    },
    {
      key: "quantityBalance",
      header: "Qty on Hand",
      width: "120px",
      cardRole: "detail",
      render: (row) => {
        if (!row.isInventory || row.isInventoryChild) return null

        const thresholdExists =
          !!row.lowQuantityBalanceThreshold ||
          row.lowQuantityBalanceThreshold === 0
        const showLowBalance =
          marinaAccess.trackInventory &&
          thresholdExists &&
          row.quantityBalance <= row.lowQuantityBalanceThreshold

        return (
          <div className="flex items-center gap-1">
            {displayQuantityBalance(row.quantityBalance)}
            {showLowBalance ? (
              <Tooltip text="Low balance" placement="top" variant="dark">
                <i className="fa-regular fa-triangle-exclamation ml-1 cursor-default text-base font-semibold text-yellow-500" />
              </Tooltip>
            ) : null}
          </div>
        )
      },
    },
  ]

  const bulkAction = () => {
    if (status === "active") {
      return {
        label: "Make inactive",
        icon: "trash-can",
        iconStyle: "solid",
        danger: true,
        onClickBulk: (selection) => {
          tracker.trackEvent(MANAGE_BULK_MAKE_INACTIVE)
          runBulk(selection, "inactive", { clearSelection: true })
        },
        confirm: {
          title: "Make items inactive",
          message: (selection) =>
            `Make ${selection.length} selected ${pluralize(
              "item",
              selection.length
            )} inactive?`,
          confirmLabel: "Make Inactive",
        },
      }
    }
    if (status === "inactive") {
      return {
        label: "Reactivate",
        icon: "arrows-rotate",
        iconStyle: "solid",
        onClickBulk: (selection) => {
          tracker.trackEvent(MANAGE_BULK_REACTIVATE)
          runBulk(selection, "active", { clearSelection: true })
        },
        confirm: {
          title: "Reactivate items",
          message: (selection) =>
            `Reactivate ${selection.length} selected ${pluralize(
              "item",
              selection.length
            )}?`,
          confirmLabel: "Reactivate",
        },
      }
    }
    return null
  }

  const ACTIONS = [
    {
      label: "Edit",
      icon: "pen-to-square",
      iconStyle: "solid",
      href: (row) => `/manage/${marinaSlug}/items/${row.id}/edit`,
      disabled: () => !canManageItems,
      onClickSingle: () => tracker.trackEvent(MANAGE_OVERFLOW_EDIT),
    },
    {
      label: "Adjust Qty",
      icon: "box-isometric",
      iconStyle: "solid",
      hidden: (row) => !row.isInventory || row.isInventoryChild,
      disabled: () => !canManageItems || !marinaAccess.trackInventory,
      onClickSingle: (row) => {
        // AdjustQuantityModal (also used by Fuel) reads snake_case product fields.
        setAdjustQuantityItem({
          id: row.id,
          name: row.name,
          product_type: row.productType,
          price_precision: row.pricePrecision,
          inventory_child_products: row.inventoryChildProducts || [],
        })
        tracker.trackEvent(MANAGE_ADJUST_QTY)
      },
    },
    {
      label: "Make inactive",
      icon: "trash-can",
      iconStyle: "solid",
      danger: true,
      hidden: (row) => !!row.deletedAt,
      disabled: (row) => !row.softDeletable || !canManageItems,
      onClickSingle: (row) => {
        tracker.trackEvent(MANAGE_OVERFLOW_MAKE_INACTIVE_CONFIRMED)
        runBulk([row.id], "inactive")
      },
      confirm: {
        title: "Make item inactive",
        message: (row) => `Are you sure you want to make ${row.name} inactive?`,
        confirmLabel: "Make Inactive",
      },
    },
    {
      label: "Reactivate",
      icon: "arrows-rotate",
      iconStyle: "solid",
      hidden: (row) => !row.deletedAt,
      disabled: () => !canManageItems || marinaAccess.activeItemLimitMet,
      onClickSingle: (row) => runBulk([row.id], "active"),
      confirm: {
        title: "Reactivate item",
        message: (row) => `Reactivate ${row.name}?`,
        confirmLabel: "Reactivate",
      },
    },
    bulkAction(),
    status === "active" &&
    marinaAccess.trackInventory &&
    canAdjustQuantitiesViaImport
      ? {
          label: "Adjust quantities",
          icon: "box-isometric",
          iconStyle: "solid",
          onClickBulk: (selection) => {
            tracker.trackEvent(MANAGE_BULK_ADJUST_QTY)
            setBulkAdjustSelection(selection)
          },
        }
      : null,
  ].filter(Boolean)

  // A row is selectable only if a currently-available bulk action can act on it:
  // deactivate (soft-deletable) or adjust quantity (standard inventory) on the
  // Active view; reactivate on the Inactive view. Default fuel items (not
  // soft-deletable, not standard) are therefore unselectable on the Active view.
  const isRowSelectable = (row) => {
    if (row.isInventoryChild) return false
    if (status === "inactive") return true
    if (status !== "active") return false

    const canDeactivate = row.softDeletable
    const canAdjust =
      marinaAccess.trackInventory &&
      row.isInventory &&
      row.productType === "standard"
    return canDeactivate || canAdjust
  }

  return (
    <>
      <div className="mb-4">
        <Heading.SectionHeader>Items</Heading.SectionHeader>
      </div>
      <ActiveItemLimitBanner />
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="w-full sm:w-1/2">
          <Form.IconTextField
            icon={
              <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
            }
            id="items-search"
            isClearable
            onChange={handleSearch}
            onClearSelection={handleClearSearch}
            onFocus={() => (searchFocused.current = true)}
            onBlur={() => (searchFocused.current = false)}
            position="left"
            value={searchTerm}
            placeholder={
              marinaAccess.barcodeScanner
                ? "Search by name or SKU, or scan barcode"
                : "Search by name or SKU"
            }
          />
        </div>
        <div className="flex items-center gap-2 sm:contents">
          <StatusFilter
            value={status}
            onChange={(next) => {
              table.setFilter("status", next)
              tracker.trackEvent(MANAGE_STATUS_FILTER, { status: next })
            }}
          />
          <div className="ml-auto">
            <NewItemButton />
          </div>
        </div>
      </div>
      <div className="pb-2 pt-3">
        <ManageItemsFilters onClearAll={handleClearAllFilters} />
      </div>
      <div className="mt-2">
        <DataTableV2
          name="manage-items-table"
          columns={COLUMNS}
          data={rows}
          actions={ACTIONS}
          isRowSelectable={isRowSelectable}
          rowConfig={{
            textColor: (row) => (row.deletedAt ? "gray" : "default"),
          }}
          emptyState={
            <span className="text-gray-500">
              {table.activeFilters.length > 0
                ? "No items match your filters"
                : "No items to show"}
            </span>
          }
          {...table.tableProps}
        />
      </div>
      {adjustQuantityItem ? (
        <AdjustQuantityModal
          item={adjustQuantityItem}
          closeModal={() => setAdjustQuantityItem(null)}
          onUpdateItem={refetchItemsData}
          accountingV2Enabled={accountingV2Enabled}
        />
      ) : null}
      {bulkAdjustSelection ? (
        <BulkQuantityAdjustmentImporter
          selection={bulkAdjustSelection}
          currentUserId={currentUserId}
          onClose={() => setBulkAdjustSelection(null)}
        />
      ) : null}
    </>
  )
}

export default ManageItemsTable
